<template>
  <div class="registration-form">
    <div class="content">
      <div class="header">
        <div class="logo-container">
          
          <img :src="require('@/assets/image/eclub-logo-1-theme.png')" class="logo" style="height: 50px;" />
        </div>
        <div class="form-title">
          <h1>Student Registration Form</h1>
          <p>Register under {{ schoolInfo.account_nameEN }}</p>
        </div>
      </div>

      <el-form :model="form" :rules="formRules" ref="form">
        <div class="form-section">
          <div class="form-row">
            <el-form-item label="Given Name (Native Language)" prop="givenName">
              <el-input v-model="form.givenName" placeholder="Please input"></el-input>
            </el-form-item>
            <el-form-item label="Last Name (Native Language)" prop="lastName">
              <el-input v-model="form.lastName" placeholder="Please input"></el-input>
            </el-form-item>
          </div>

          <div class="form-row">
            <el-form-item label="Given Name (English)" prop="givenName_pinyin">
              <el-input v-model="form.givenName_pinyin" placeholder="Please input"></el-input>
            </el-form-item>
            <el-form-item label="Last Name (English)" prop="lastName_pinyin">
              <el-input v-model="form.lastName_pinyin" placeholder="Please input"></el-input>
            </el-form-item>
          </div>

          <div class="form-row">
            <el-form-item label="Email" prop="email">
              <el-input v-model="form.email" placeholder="Please input"></el-input>
            </el-form-item>
            <el-form-item label="Grade" prop="grade">
              <el-select v-model="form.grade" placeholder="Please select">
                <el-option 
                  v-for="grade in grades" 
                  :key="grade.value" 
                  :label="grade.label" 
                  :value="grade.value">
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <div class="form-row">
            <el-form-item label="Guardian's Email" prop="guardian_email">
              <el-input v-model="form.guardian_email" placeholder="Please input"></el-input>
              <p class="helper-text">The guardian's email serves as a backup for communication if necessary.</p>
            </el-form-item>
          </div>
        </div>
      </el-form>

      <div class="terms">
        <el-checkbox v-model="form.agreement">
          I agree that my information may be used to set up a student account and to receive notifications about club activities.
        </el-checkbox>
      </div>

      <div class="submit-section">
        <el-button type="primary" @click="submitForm" :loading="submitting">
          Submit Registration
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import { getUserType } from '@/utils/store'

export default {
  name: 'StudentRegistration',
  beforeRouteEnter(to, from, next) {
    const userType = getUserType()
    if (userType === 'advisor') {
      next('/home/adviserclubs')
    } else if (userType === 'schoolAdmin') {
      next('/home/schoolclubs')
    } else {
      next()
    }
  },
  data() {
    return {
      schoolInfo: {},
      submitting: false,
      form: {
        givenName: '',
        lastName: '',
        givenName_pinyin: '',
        lastName_pinyin: '',
        email: '',
        grade: '',
        guardian_email: '',
        agreement: false
      },
      grades: [
        { value: '9', label: 'Grade 9' },
        { value: '10', label: 'Grade 10' },
        { value: '11', label: 'Grade 11' },
        { value: '12', label: 'Grade 12' }
      ],
      formRules: {
        givenName: [
          { required: true, message: 'Please input given name', trigger: 'blur' }
        ],
        lastName: [
          { required: true, message: 'Please input last name', trigger: 'blur' }
        ],
        givenName_pinyin: [
          { required: true, message: 'Please input English given name', trigger: 'blur' }
        ],
        lastName_pinyin: [
          { required: true, message: 'Please input English last name', trigger: 'blur' }
        ],
        email: [
          { required: true, message: 'Please input email', trigger: 'blur' },
          { type: 'email', message: 'Please input valid email', trigger: 'blur' }
        ],
        grade: [
          { required: true, message: 'Please select grade', trigger: 'change' }
        ],
        guardian_email: [
          { required: true, message: 'Please input guardian email', trigger: 'blur' },
          { type: 'email', message: 'Please input valid email', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.fetchSchoolInfo()
  },
  methods: {
    async fetchSchoolInfo() {
      try {
        const response = await postRequest('getSchoolInfo', {
          school_id: this.$route.query.school_id
        })
        if (response.data.code === 0) {
          this.schoolInfo = response.data.data
        }
      } catch (error) {
        this.$message.error('Failed to load school information')
      }
    },
    submitForm() {
      if (!this.form.agreement) {
        this.$message.warning('Please agree to the terms first')
        return
      }

      this.$refs.form.validate(async (valid) => {
        if (valid) {
          this.submitting = true
          try {
            const response = await postRequest('createStudentAccount', {
              ...this.form,
              account_id: this.$route.query.school_id
            })
            
            if (response.data.code === 0) {
              this.$message.success('Registration successful')
              this.$router.push('/registration-success')
            }
          } catch (error) {
            this.$message.error('Registration failed')
          } finally {
            this.submitting = false
          }
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.registration-form {
  min-height: 100vh;
  background-color: #fa6a51;
  padding: 20px;

  .content {
    max-width: 1000px;
    margin: 0 auto;
    background: white;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

    .header {
      display: flex;
      align-items: flex-start;
      margin-bottom: 40px;

      .logo-container {
        flex: 1;
        display: flex;
        gap: 20px;
        
        .logo {
          height: 60px;
          width: auto;
        }
      }

      .form-title {
        text-align: right;

        h1 {
          color: #fa6a51;
          font-size: 28px;
          margin: 0;
        }

        p {
          color: #666;
          margin-top: 8px;
        }
      }
    }

    .form-section {
      .form-row {
        display: flex;
        gap: 20px;
        margin-bottom: 20px;
        align-items: flex-start;

        .el-form-item {
          flex: 1;
          
          ::v-deep .el-input,
          ::v-deep .el-select {
            width: 100%;
            .el-input__inner {
              height: 40px;
              line-height: 40px;
            }
          }
        }
      }
    }

    .helper-text {
      font-size: 12px;
      color: #909399;
      margin-top: 4px;
    }

    .terms {
      margin: 40px 0;
      text-align: center;
    }

    .submit-section {
      text-align: center;

      .el-button {
        width: 200px;
        height: 45px;
        border-radius: 22px;
      }
    }
  }
}

::v-deep {
  .el-input__inner {
    border-color: #fa6a51;
  }

  .el-button--primary {
    background-color: #fa6a51;
    border-color: #fa6a51;
  }
}
</style> 